/**
 * useProfileImageUpload - Hook for handling profile image uploads
 * 
 * Provides functionality to upload profile images with loading states,
 * error handling, and automatic profile refresh after successful upload.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { uploadApi } from '../lib/api';

interface UseProfileImageUploadResult {
  uploadImage: (file: File) => Promise<void>;
  isUploading: boolean;
  error: string | null;
  reset: () => void;
}

export const useProfileImageUpload = (): UseProfileImageUploadResult => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (file: File) => {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('Please select an image file');
      }

      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        throw new Error('Image must be smaller than 5MB');
      }

      const result = await uploadApi.uploadProfileImage(file);
      
      if (!result.success) {
        throw new Error(result.message || 'Upload failed');
      }

      return result;
    },
    onSuccess: () => {
      // Refresh profile data to show updated image
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['auth', 'user'] });
    },
  });

  return {
    uploadImage: async (file: File) => {
      await mutation.mutateAsync(file);
    },
    isUploading: mutation.isPending,
    error: mutation.error?.message || null,
    reset: mutation.reset,
  };
};
